import React, { useMemo } from 'react';
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  type Edge,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useGraphEntities, useGraphRelationships } from '../../api/hooks/knowledge.hook';

type GraphNodeData = {
  label: string;
  entityType: string;
};

const typeColor: Record<string, string> = {
  Project: '#3b82f6',
  Person: '#22c55e',
  Methodology: '#f59e0b',
  Industry: '#0ea5e9',
  Document: '#737373',
};

const preferredOrder = ['Project', 'Person', 'Methodology', 'Industry', 'Document'];

const formatLabel = (raw: string) =>
  raw
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const GraphInner: React.FC = () => {
  const entitiesQuery = useGraphEntities(undefined, 320);
  const relationshipsQuery = useGraphRelationships(undefined, 480);

  const { nodes, edges } = useMemo(() => {
    const entities = entitiesQuery.data?.entities || [];
    const relationships = relationshipsQuery.data?.relationships || [];

    const grouped = new Map<string, Node<GraphNodeData>[]>();
    const nameToId = new Map<string, string>();

    entities.forEach((entity, index) => {
      const entityType = entity.types?.[0] || 'Entity';
      const name = String(entity.name || '').trim();
      if (!name) {
        return;
      }

      const nodeId = `${entityType}:${name}:${index}`;
      if (!nameToId.has(name)) {
        nameToId.set(name, nodeId);
      }

      const list = grouped.get(entityType) || [];
      list.push({
        id: nodeId,
        data: {
          label: name,
          entityType,
        },
        position: { x: 0, y: 0 },
        style: {
          border: `1px solid ${typeColor[entityType] || '#d4d4d8'}`,
          borderLeft: `4px solid ${typeColor[entityType] || '#d4d4d8'}`,
          borderRadius: 12,
          padding: 10,
          background: '#ffffff',
          width: 220,
          fontSize: 12,
          color: '#171717',
        },
      });
      grouped.set(entityType, list);
    });

    const orderedTypes = [
      ...preferredOrder.filter((type) => grouped.has(type)),
      ...Array.from(grouped.keys())
        .filter((type) => !preferredOrder.includes(type))
        .sort((a, b) => a.localeCompare(b)),
    ];

    const layoutedNodes: Node<GraphNodeData>[] = [];
    orderedTypes.forEach((type, columnIndex) => {
      const list = (grouped.get(type) || []).sort((a, b) =>
        a.data.label.localeCompare(b.data.label),
      );
      list.forEach((node, rowIndex) => {
        layoutedNodes.push({
          ...node,
          position: {
            x: 40 + columnIndex * 260,
            y: 30 + rowIndex * 88,
          },
        });
      });
    });

    const layoutedEdges: Edge[] = relationships
      .slice(0, 500)
      .map((relationship, index) => {
        const source = nameToId.get(String(relationship.source || ''));
        const target = nameToId.get(String(relationship.target || ''));
        if (!source || !target) {
          return null;
        }
        return {
          id: `rel:${source}:${relationship.type}:${target}:${index}`,
          source,
          target,
          label: formatLabel(String(relationship.type || 'related_to')),
          labelStyle: { fontSize: 10, fill: '#737373' },
          style: { stroke: '#d4d4d8', opacity: 0.85 },
        } as Edge;
      })
      .filter(Boolean) as Edge[];

    return {
      nodes: layoutedNodes,
      edges: layoutedEdges,
    };
  }, [entitiesQuery.data?.entities, relationshipsQuery.data?.relationships]);

  if (entitiesQuery.isLoading || relationshipsQuery.isLoading) {
    return (
      <div className="flex h-[460px] items-center justify-center text-sm text-neutral-500">
        Loading graph…
      </div>
    );
  }

  if (nodes.length === 0) {
    return (
      <div className="flex h-[460px] items-center justify-center text-sm text-neutral-500">
        No entities indexed yet.
      </div>
    );
  }

  return (
    <div className="h-[460px] rounded-2xl border border-neutral-200 bg-neutral-50">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        minZoom={0.1}
        maxZoom={1.8}
        defaultEdgeOptions={{ animated: false }}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={20} size={1} color="#d4d4d8" />
        <Controls showInteractive={false} />
        <MiniMap pannable zoomable className="!rounded-lg !border !border-neutral-200 !bg-white" />
      </ReactFlow>
    </div>
  );
};

const KnowledgeGraphOverview: React.FC = () => {
  return (
    <ReactFlowProvider>
      <GraphInner />
    </ReactFlowProvider>
  );
};

export default KnowledgeGraphOverview;
